import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, InputAdornment, MenuItem, Pagination, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdCheckCircle, MdClose, MdGppBad, MdGroups, MdLogout, MdRefresh, MdSearch, MdVisibility } from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';

const FONT = 'Mulish, sans-serif';
const LIMIT = 20;

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const kind = (a) => {
  const l = String(a || '').toLowerCase();
  if (l.includes('fail')) return { bg: '#fee2e2', fg: '#991b1b', accent: '#ef4444', text: 'Failed' };
  if (l.includes('success')) return { bg: '#dcfce7', fg: '#166534', accent: '#22c55e', text: 'Success' };
  if (l.includes('logout')) return { bg: '#dbeafe', fg: '#1e40af', accent: '#3b82f6', text: 'Logout' };
  return { bg: '#e2e8f0', fg: '#334155', accent: '#64748b', text: a || '—' };
};
const detailsOf = (log) => (log?.details && typeof log.details === 'object' ? log.details : {});
const who = (log) => {
  const u = log.userId;
  const name = u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : '';
  return { name: name || u?.email || detailsOf(log).email || 'Unknown', email: u?.email || detailsOf(log).email || '' };
};
const dt = (d) => new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
const browser = (ua) => {
  if (!ua) return 'Unknown';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return ua.slice(0, 24);
};
const os = (ua) => {
  if (!ua) return 'Unknown';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Android/i.test(ua)) return 'Android';
  if (/iPhone|iPad|iOS/i.test(ua)) return 'iOS';
  if (/Mac OS/i.test(ua)) return 'macOS';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Unknown';
};
const device = (ua) => (/Mobile|Android|iPhone/i.test(ua || '') ? 'Mobile' : 'Desktop');

const StatusChip = ({ action }) => {
  const k = kind(action);
  return <Chip size="small" label={action || '—'} sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 11, height: 22, textTransform: 'uppercase', background: k.bg, color: k.fg }} />;
};

const Kpi = ({ title, value, icon: Icon, from, to }) => (
  <Box sx={{ flex: 1, minWidth: 170, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '12px', p: 1.1, display: 'flex' }}><Icon size={22} /></Box>
    <Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
    </Box>
  </Box>
);

const EMPTY = { action: '', userId: '', from: '', to: '' };

const LoginLogs = () => {
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ successToday: 0, failedToday: 0, logoutsToday: 0, activeUsersToday: 0 });
  const [facets, setFacets] = useState({ actions: [], users: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filters, setFilters] = useState(EMPTY);
  const [selected, setSelected] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: LIMIT, search: debounced, ...filters };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData('logs/login', params, authToken(), false);
      if (mine !== seq.current) return;
      setLogs(res.logs || []);
      setMeta({ total: res.meta?.total || 0, totalPages: res.meta?.totalPages || 1 });
      if (res.stats) setStats(res.stats);
      if (res.facets) setFacets(res.facets);
    } catch {
      if (mine === seq.current) setError('Failed to fetch login logs. Please try again.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, filters]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const pickAction = (a) => setF('action', filters.action === a ? '' : a);
  const dirty = !!search || Object.values(filters).some(Boolean);
  const setRange = (days) => {
    const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    const start = new Date();
    start.setDate(start.getDate() - days);
    setFilters((f) => ({ ...f, from: iso(start), to: iso(new Date()) }));
    setPage(1);
  };

  const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
  const extra = selected ? Object.entries(detailsOf(selected)).filter(([k]) => k !== 'email') : [];
  const sel = selected ? kind(selected.action) : null;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Login logs</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Sign-ins, failed attempts and sign-outs.</Typography>
        </Box>
        <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading} sx={{ background: '#f1f5f9', borderRadius: '10px' }}><MdRefresh size={20} /></IconButton></span></Tooltip>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Box sx={{ flex: 1, minWidth: 170, cursor: 'pointer', outline: filters.action === 'Login Success' ? '3px solid #0f172a' : 'none', outlineOffset: 2, borderRadius: '16px', display: 'flex' }} onClick={() => pickAction('Login Success')}>
          <Kpi title="Successful today" value={stats.successToday} icon={MdCheckCircle} from="#22c55e" to="#15803d" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 170, cursor: 'pointer', outline: filters.action === 'Failed login' ? '3px solid #0f172a' : 'none', outlineOffset: 2, borderRadius: '16px', display: 'flex' }} onClick={() => pickAction('Failed login')}>
          <Kpi title="Failed today" value={stats.failedToday} icon={MdGppBad} from="#ef4444" to="#b91c1c" />
        </Box>
        <Kpi title="Sign-outs today" value={stats.logoutsToday} icon={MdLogout} from="#3b82f6" to="#1d4ed8" />
        <Kpi title="Users signed in today" value={stats.activeUsersToday} icon={MdGroups} from="#6366f1" to="#4338ca" />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search email or IP…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Event" value={filters.action} onChange={(e) => setF('action', e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">All events</MenuItem>
          {facets.actions.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="User" value={filters.userId} onChange={(e) => setF('userId', e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">All users</MenuItem>
          {facets.users.map((u) => <MenuItem key={u._id} value={u._id}>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}</MenuItem>)}
        </TextField>
        <TextField size="small" type="date" label="From" value={filters.from} onChange={(e) => setF('from', e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="To" value={filters.to} onChange={(e) => setF('to', e.target.value)} InputLabelProps={{ shrink: true }} />
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
          {[['Today', 0], ['7 days', 7], ['30 days', 30]].map(([t, d]) => (
            <Chip key={t} label={t} size="small" clickable onClick={() => setRange(d)} sx={{ fontFamily: FONT, fontWeight: 700 }} />
          ))}
        </Box>
        {dirty && <Button onClick={() => { setSearch(''); setFilters(EMPTY); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>When</TableCell>
                <TableCell sx={head}>User</TableCell>
                <TableCell sx={head}>Event</TableCell>
                <TableCell sx={head}>Device</TableCell>
                <TableCell sx={head}>IP address</TableCell>
                <TableCell sx={head} align="right">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => <TableRow key={i}>{[...Array(6)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && logs.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>No login events match these filters.</TableCell></TableRow>
              )}
              {!loading && logs.map((log) => {
                const w = who(log);
                return (
                  <TableRow key={log._id} hover>
                    <TableCell sx={{ fontFamily: FONT, fontSize: 13 }}>
                      <b>{new Date(log.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</b>
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{new Date(log.createdAt).toLocaleTimeString('en-IN')}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONT, fontSize: 13 }}>
                      <b>{w.name}</b>
                      {w.email && w.email !== w.name && <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{w.email}</Typography>}
                    </TableCell>
                    <TableCell><StatusChip action={log.action} /></TableCell>
                    <TableCell sx={{ fontFamily: FONT, fontSize: 13 }}>
                      {browser(log.userAgent)} · {os(log.userAgent)}
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{device(log.userAgent)}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>{log.ipAddress || '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View details"><IconButton size="small" onClick={() => setSelected(log)} sx={{ color: '#4f46e5', background: '#4f46e514', borderRadius: '8px' }}><MdVisibility size={17} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} event{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" disabled={loading} />
        </Box>
      </Box>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        {selected && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: `linear-gradient(135deg,${sel.accent},${sel.accent}cc)`, color: '#fff' }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 19 }}>{selected.action}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.9 }}>{dt(selected.createdAt)}</Typography>
              </Box>
              <IconButton onClick={() => setSelected(null)} size="small" sx={{ color: '#fff' }}><MdClose size={20} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                ['Account', '#6366f1', [['Name', who(selected).name], ['Email', who(selected).email]]],
                ['Device & network', '#0ea5e9', [['IP address', selected.ipAddress], ['Browser', browser(selected.userAgent)], ['Operating system', os(selected.userAgent)], ['Device type', device(selected.userAgent)]]],
              ].map(([title, color, rows]) => (
                <Box key={title} sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${color}`, borderRadius: '14px', p: 2.25 }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>{title}</Typography>
                  {rows.map(([k, v]) => (
                    <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px dashed #e2e8f0' }}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{k}</Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, textAlign: 'right', wordBreak: 'break-all' }}>{v || '—'}</Typography>
                    </Box>
                  ))}
                </Box>
              ))}
              {extra.length > 0 && (
                <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${sel.accent}`, borderRadius: '14px', p: 2.25 }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>Other details</Typography>
                  {extra.map(([k, v]) => (
                    <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px dashed #e2e8f0' }}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', textTransform: 'capitalize' }}>{k.replace(/([A-Z])/g, ' $1')}</Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
              {selected.userAgent && (
                <Typography sx={{ fontFamily: 'monospace', fontSize: 11.5, color: '#64748b', wordBreak: 'break-all' }}>{selected.userAgent}</Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setSelected(null)} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, '&:hover': { background: 'rgb(255 190 0)' } }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default LoginLogs;
